import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {languageActions, sessionActions, toggleTheme} from './store';
import { useEffectAsync } from './reactHelper';
import { organizationActions } from "./store"
import { languages, t } from "./common/LocalizationProvider";
import NotificationsMenu from "./shared/NotificationsMenu";
import ConfirmHandler from "./shared/ConfirmHandler";

import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tooltip from "@material-ui/core/Tooltip";
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import NotificationsIcon from '@material-ui/icons/Notifications';
import Badge from '@material-ui/core/Badge';
import { Avatar, FormControl, Select, Switch, useTheme } from '@material-ui/core';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MoreIcon from "@material-ui/icons/MoreVert";
import {Language, Translate, WbSunny} from "@material-ui/icons";
import {cyberStyles} from "./cssShared/cyberTheme";
import Brightness4Icon from '@material-ui/icons/Brightness4';

const toBlob = (base64Data) => {
    const binaryData = atob(base64Data);
    const arrayBuffer = new ArrayBuffer(binaryData.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i);
    }
    return new Blob([arrayBuffer], { type: 'image/png' });
}

const useStyles = makeStyles(theme => ({
    ...cyberStyles(theme),
    logoSmall: {
        width: 50,
        height: 50,
    },
    listTools: {
        paddingLeft: theme.spacing(4)
    },
    sectionDesktop: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
            alignItems: 'center',
        },
        paddingLeft: theme.spacing(2)
    },
    sectionMobile: {
        display: 'flex',
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    linkDecoration: {
        textDecoration: 'none'
    },
    logo: {
        width: '32px',
        height: '32px',
        objectFit: 'contain',
    },
    prifileMargin: {
        marginRight: '30px',
        [theme.breakpoints.down('sm')]: {
            marginRight: '12px',
        },
    },
}));

const MainToolbar = () => {
    const classes = useStyles();
    const location = useLocation();
    const theme = useTheme();
    const dispatch = useDispatch();

    const user = useSelector(state => state.session.user);
    const languageSelected = useSelector(state => state.language.language);
    const organizationInfo =  useSelector(state => state.organization_information.org ? state.organization_information.org : false);
    const notifications = useSelector(state => Object.values(state.notifications.notifications));

    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

    const adminEnabled = user.administrator;
    const userId = user.id;

    const [notificationsAnchor, setNotificationsAnchor] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(null);
    const [langMenuOpen, setLangMenuOpen] = useState(false);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

    const unseenNotificationCount = useMemo(() => notifications.filter(n => n.unseen).length, [notifications]);

    const orgLogoUrl = useMemo(() => {
        if (organizationInfo?.[0]?.logo){
            const blob = toBlob(organizationInfo[0].logo);
            return URL.createObjectURL(blob);
        }
        return null
    }, [organizationInfo])

    useEffectAsync(async () => {
        if (!organizationInfo) {
            const response = await fetch('/api/users/getorg');
            if (response.ok)
                dispatch(organizationActions.updateOrg(await response.json()))
        }
    }, []);

    const handleLanguageChange = (e) => dispatch(languageActions.updateLanguage(e.target.value));

    const toggleMapType = () => {
        const updatedUser  = {...user, map: user.map === 'googleMap' ? 'osm' : 'googleMap'}
        dispatch(sessionActions.updateUser(updatedUser))
    }

    const handleLogoutDialogClose = () => {
        setLogoutDialogOpen(false)
    }

    const isDark = useSelector(state => state.theme.mode); // Get current mode

    return (
        <>
            <AppBar position="static" className={classes.appBar}>
                <Toolbar className={!(location.pathname === '/') && !isDesktop ? classes.listTools : ''}>

                    <Tooltip title={t('profile')} className={classes.prifileMargin} >
                        <Link to={"/user/" + userId} className={classes.linkDecoration}>
                            <Avatar className={classes.avatar}>
                                {adminEnabled && <img src='/tracthing.png' alt="SuperAdmin Logo" className={classes.logo}/>}
                                {organizationInfo?.[0] && (orgLogoUrl ?
                                        <img src={orgLogoUrl} alt="Organization Logo" className={classes.logo}/>
                                        :
                                        <span className={classes.logoTitle}>{organizationInfo[0].organization.charAt(0).toUpperCase()}</span>
                                )}
                            </Avatar>
                        </Link>
                    </Tooltip>

                    {isDesktop && <Typography variant="h6" color="inherit" style={{ fontWeight: 900 }}>TRACTHING</Typography>}

                    {(organizationInfo?.[0]?.organization || adminEnabled) &&
                        <Typography variant="h6" color="inherit" className={classes.flexOrgName}>
                            {organizationInfo?.[0]?.organization.toUpperCase() || (adminEnabled && (t('superAdmin')))}
                        </Typography>
                    }

                    <div className={classes.sectionDesktop} >
                        <IconButton color="inherit" onClick={() => dispatch(toggleTheme())}>
                            {isDark === 'dark' ? <WbSunny className={classes.iconMenuItemColor}/> : <Brightness4Icon className={classes.iconMenuItemColor}/>}
                        </IconButton>
                        {adminEnabled && <Tooltip title={'Google Maps HUD'}>
                            <Switch
                                color="secondary"
                                checked={user.map === 'googleMap'}
                                onChange={toggleMapType}
                                inputProps={{'aria-label': 'map switch'}}
                            />
                        </Tooltip>}

                        <Tooltip title={t('loginLanguage')} >
                            <IconButton className={classes.iconButton} onClick={() => setLangMenuOpen(!langMenuOpen)}>
                                <Translate size="small" />
                            </IconButton>
                        </Tooltip>

                        {langMenuOpen && (
                            <Select
                                className={classes.languageSelect}
                                onChange={handleLanguageChange}
                                open={langMenuOpen}
                                onClose={() => setLangMenuOpen(false)}
                                MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
                            >
                                {languages.map((it) => <MenuItem key={it.code} value={it.code} className={classes.menuItem}>{it.name}</MenuItem>)}
                            </Select>
                        )}

                        <Tooltip title={t('sharedNotifications')} >
                            <IconButton className={classes.iconButton} aria-label="show all new notifications" onClick={(event) => setNotificationsAnchor(event.currentTarget)}>
                                <Badge badgeContent={unseenNotificationCount} className={classes.badge} overlap="rectangular">
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t("exit")}>
                            <IconButton className={classes.iconButton} onClick={() => setLogoutDialogOpen(true)} >
                                <ExitToAppIcon />
                            </IconButton>
                        </Tooltip>
                    </div>

                    <div className={classes.sectionMobile}>
                        <IconButton
                            onClick={(event) => setMobileMenuOpen(event.currentTarget)}
                            className={classes.iconButton}
                        >
                            <MoreIcon />
                        </IconButton>
                    </div>
                </Toolbar>
            </AppBar>

            <NotificationsMenu
                openNotsList={notificationsAnchor}
                onClose={() =>  setNotificationsAnchor(null)}
                notifications={notifications}
            />

            <Menu
                anchorEl={mobileMenuOpen}
                className={classes.mobileMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(mobileMenuOpen)}
                onClose={() => setMobileMenuOpen(null)}
            >
                <MenuItem className={classes.menuItem}>
                    <Language style={{ marginRight: "10px", color: '#00f2ff' }} />
                    <FormControl variant="filled" size="small">
                        <Select
                            value={languageSelected || 'en'}
                            onChange={handleLanguageChange}
                            className={classes.languageSelect}
                            disableUnderline
                        >
                            {languages.map((it) => <MenuItem key={it.code} value={it.code} className={classes.menuItem}>{it.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </MenuItem>
                <MenuItem onClick={(event) => setNotificationsAnchor(event.currentTarget)} className={classes.menuItem}>
                    <IconButton color="inherit">
                        <Badge badgeContent={unseenNotificationCount} className={classes.badge} overlap="rectangular">
                            <NotificationsIcon />
                        </Badge>
                    </IconButton>
                    <p>{t("sharedNotifications")}</p>
                </MenuItem>
                <MenuItem onClick={() => setLogoutDialogOpen(true)} className={classes.menuItem}>
                    <IconButton color="inherit">
                        <ExitToAppIcon />
                    </IconButton>
                    <p>{t("exit")}</p>
                </MenuItem>
            </Menu>
            <ConfirmHandler open={logoutDialogOpen} endpoint="exit" handleCloseDialog={handleLogoutDialogClose}/>
        </>
    );
}

export default MainToolbar;